import { BASE_URL } from 'config';
import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Button,
  TextField,
  Avatar,
  IconButton,
  Paper,
  Grid,
  Badge,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PersonIcon from "@mui/icons-material/Person";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController, setDirection } from "context";

export default function ChatInterface() {
  const [controller, dispatch] = useMaterialUIController();
  const [activeChat, setActiveChat] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // null | 'success' | 'error'

  // جلب رسائل محادثة معينة
  const fetchMessages = async (conversationId) => {
    setLoading(true);
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(
        `${BASE_URL}/chats/messages/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      // إضافة علامة الصح تلقائياً للرسائل المرسلة من المستخدم
      const userId = controller?.user?.id || 12; // عدل حسب نظامك
      const msgs = (data.data || []).map((msg) =>
        msg.is_sender ? { ...msg, _sendStatus: "success" } : msg
      );
      setMessages(msgs);
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // جلب المحادثات من الـ API
  const fetchChats = async () => {
    setLoading(true);
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(BASE_URL + "/chats", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setChats(data.data || []);
    } catch (error) {
      setChats([]);
    } finally {
      setLoading(false);
    }
  };

  // حالة تحميل منفصلة للرسائل فقط
  const [loadingMessages, setLoadingMessages] = useState(false);
  const fetchMessagesOnly = async (conversationId) => {
    setLoadingMessages(true);
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(
        `${BASE_URL}/chats/messages/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      // إضافة علامة الصح تلقائياً للرسائل المرسلة من المستخدم
      const userId = controller?.user?.id || 12; // عدل حسب نظامك
      const msgs = (data.data || []).map((msg) =>
        msg.is_sender ? { ...msg, _sendStatus: "success" } : msg
      );
      setMessages(msgs);
    } catch (error) {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // إرسال رسالة فعلية
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !activeChat) return;
    // إضافة رسالة مؤقتة مع حالة إرسال
    const tempId = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        content: { text: messageInput },
        is_sender: true,
        created_at: new Date().toISOString(),
        _sending: true,
        _sendStatus: null,
      },
    ]);
    setMessageInput("");
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(BASE_URL + "/chats/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: activeChat,
          user_id: controller?.user?.id || 12, // عدل حسب نظامك
          content: {
            type: "text",
            text: messageInput,
          },
          message_type: "text",
        }),
      });
      const data = await response.json();
      if (data.status) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? { ...msg, _sending: false, _sendStatus: "success", _fixed: true }
              : msg
          )
        );
        // لا تعيد جلب الرسائل بعد النجاح
      } else {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? { ...msg, _sending: false, _sendStatus: "error", _fixed: true }
              : msg
          )
        );
      }
    } catch (error) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId ? { ...msg, _sending: false, _sendStatus: "error", _fixed: true } : msg
        )
      );
    }
  };

  useEffect(() => {
    setDirection(dispatch, "rtl");
    fetchChats();
    return () => setDirection(dispatch, "ltr");
  }, []);

  const getActiveChat = () => {
    return chats.find((chat) => chat.id === activeChat) || {};
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container spacing={2} sx={{ height: "calc(100vh - 100px)", mt: 1 }}>
        {/* Sidebar - Chats List */}
        <Grid item xs={12} md={5} lg={4}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                المحادثات
              </Typography>
            </CardContent>
            <Divider />
            <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 0, maxWidth: "100%" }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography>جاري تحميل المحادثات...</Typography>
                </Box>
              ) : (
                <List sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                  {chats.map((chat) => {
                    const customer = chat.customer;
                    const avatarUrl = customer?.avatar;
                    const displayName = customer?.name || `مستخدم رقم ${chat.initiator_id}`;
                    const lastMsg = chat.last_message;
                    let lastMsgText = lastMsg?.content?.text || "-";
                    let lastMsgDisplay = lastMsg?.is_sender ? `أنت: ${lastMsgText}` : lastMsgText;
                    return (
                      <ListItem
                        key={chat.id}
                        button
                        selected={activeChat === chat.id}
                        onClick={() => {
                          setActiveChat(chat.id);
                          fetchMessagesOnly(chat.id);
                        }}
                        sx={{
                          flexDirection: "row",
                          alignItems: "center",
                          bgcolor: activeChat === chat.id ? "action.selected" : "inherit",
                          borderRadius: 2,
                          mb: 1,
                          mx: 1,
                          width: "100%",
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          p: 1,
                        }}
                      >
                        <Avatar
                          src={avatarUrl || undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            bgcolor: avatarUrl ? undefined : "grey.300",
                          }}
                        >
                          {!avatarUrl && <PersonIcon sx={{ color: "#888" }} />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "100%",
                              maxWidth: "100%",
                              display: "block",
                            }}
                          >
                            {displayName}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "100%",
                              maxWidth: "100%",
                              display: "block",
                            }}
                          >
                            {lastMsgDisplay}
                          </Typography>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Card>
        </Grid>
        {/* Chat Area */}
        <Grid item xs={12} md={7} lg={8} sx={{ display: "flex", flexDirection: "column" }}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {activeChat ? (
              <>
                <CardContent
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {(() => {
                    const chat = chats.find((c) => c.id === activeChat);
                    const customer = chat?.customer;
                    const avatarUrl = customer?.avatar;
                    const displayName = customer?.name || `مستخدم رقم ${chat?.initiator_id || "-"}`;
                    return (
                      <>
                        <Avatar
                          src={avatarUrl || undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            bgcolor: avatarUrl ? undefined : "grey.300",
                          }}
                        >
                          {!avatarUrl && <PersonIcon sx={{ color: "#888" }} />}
                        </Avatar>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {displayName}
                        </Typography>
                      </>
                    );
                  })()}
                </CardContent>
                <Divider />
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    bgcolor: "grey.100",
                    maxHeight: "350px",
                  }}
                >
                  {loadingMessages ? (
                    <Typography>جاري تحميل الرسائل...</Typography>
                  ) : messages.length === 0 ? (
                    <Typography color="text.secondary">لا توجد رسائل</Typography>
                  ) : (
                    messages.map((msg, index) => (
                      <Box
                        key={msg.id || index}
                        sx={{
                          display: "flex",
                          justifyContent: msg.is_sender ? "flex-end" : "flex-start",
                          mb: 1,
                        }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            maxWidth: "70%",
                            p: 1.5,
                            borderRadius: msg.is_sender
                              ? "16px 4px 16px 16px"
                              : "4px 16px 16px 16px",
                            bgcolor: msg.is_sender ? "primary.main" : "background.paper",
                            color: msg.is_sender ? "white" : "text.primary",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2">{msg.content?.text || ""}</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.5,
                              display: "block",
                              color: msg.is_sender ? "primary.contrastText" : "text.secondary",
                              ml: 1,
                            }}
                          >
                            {msg.created_at
                              ? (() => {
                                  const d = new Date(msg.created_at);
                                  const date = d.toLocaleDateString("ar-EG");
                                  const time = d.toLocaleTimeString("ar-EG", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  });
                                  return `${date}  ${time}`;
                                })()
                              : ""}
                          </Typography>
                          {msg._sending && (
                            <Box ml={1} display="inline-flex" alignItems="center">
                              <CircularProgress size={16} />
                            </Box>
                          )}
                          {msg._sendStatus === "success" && (
                            <Box
                              ml={1}
                              color="success.main"
                              display="inline-flex"
                              alignItems="center"
                            >
                              <span style={{ fontSize: 14 }}>✔️</span>
                            </Box>
                          )}
                          {msg._sendStatus === "error" && (
                            <Box
                              ml={1}
                              color="error.main"
                              display="inline-flex"
                              alignItems="center"
                            >
                              <span style={{ fontSize: 14 }}>❌</span>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    ))
                  )}
                </Box>
                <Divider />
                <Box sx={{ p: 2, bgcolor: "grey.50", borderTop: 1, borderColor: "divider" }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs>
                      <TextField
                        fullWidth
                        placeholder="اكتب رسالتك هنا..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                        variant="outlined"
                        size="small"
                        sx={{ bgcolor: "grey.100" }}
                      />
                    </Grid>
                    <Grid item>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton color="primary" onClick={handleSendMessage} disabled={sending}>
                          {sending ? (
                            <CircularProgress size={22} sx={{ color: "white" }} />
                          ) : (
                            <SendIcon />
                          )}
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "white",
                }}
              >
                <Box textAlign="center" color="text.secondary">
                  <ChatBubbleOutlineIcon sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6">اختر محادثة لعرض الرسائل</Typography>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
}
